/**
 * A person, as a small round thing.
 *
 * Falls back to initials rather than a silhouette. A generic avatar next to
 * three real photographs looks like a loading failure; initials on the accent
 * tint look deliberate, and they carry more information.
 */

import { useState } from 'react';

export function Avatar({
  name,
  src,
  size = 36,
  className = '',
}: {
  name: string;
  src?: string | null;
  size?: number;
  className?: string;
}) {
  // A photo that 404s must not leave an empty circle; falling back on error is
  // the only reliable way to know, because Google's URLs are opaque.
  const [failed, setFailed] = useState(false);
  const showImage = src && !failed;

  return (
    <span
      className={`avatar ${className}`.trim()}
      style={{ '--size': `${size}px` } as React.CSSProperties}
      aria-hidden="true"
      title={name}
    >
      {showImage ? (
        <img src={src} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} />
      ) : (
        initials(name)
      )}
    </span>
  );
}

/**
 * One or two letters.
 *
 * First and last word rather than first two, so "Samuel Lee Ray" gives SR.
 * An email address falls back to its first character.
 */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return (words[0]![0] ?? '?').toUpperCase();
  return ((words[0]![0] ?? '') + (words[words.length - 1]![0] ?? '')).toUpperCase();
}
